import * as moment from "moment-timezone";
import { useCallback } from "react";

import { IProductLeadTimeGroup } from "src/core/products/api/products.api";

import useCutoffByDate from "./useCutoffByDate";

/**
 * Get the number of days until a cutoff time.
 *
 * @param {String} deliveryDate - the order delivery date.
 * @param {String} cutOffTime - The customer cutoff time - e.g. 14.00h.
 * @param {Number} productCutOff - lead time that should be used for the calculation.
 * @param {Object} weekLeadTimes - ordering lead time for each day of the week (it
 *  reflects the cutoff week day of an order with a product with leadTime = 1 need).
 * { 1: 6, 2: 1, 3: 2, 4: 3, 5: 4... } means:
 *   - Cutoff for delivery on Monday is Friday.
 *   - Cutoff for delivery on Tuesday is Monday.
 *   - Cutoff for delivery on Wednesday is Tuesday.
 *   - and so on.
 *
 * @return {{ days: number, errorCode?: string }} -
 *   The number of days until the cutoff time and the corresponding error message
 *   when there is not enough time for delivering daysToCutOff is <= 0.
 */
const useDaysToCutoff = () => {
  const cutoffByDate = useCutoffByDate();

  const daysToCutOff = useCallback(
    (deliveryDate: Date, productLeadTimeGroup: IProductLeadTimeGroup) => {
      const cutoff = cutoffByDate(deliveryDate, "shortest", productLeadTimeGroup);
      const orderCutOffDays = moment.utc(cutoff).diff(moment.utc(), "day");

      if (orderCutOffDays === 0) {
        if (moment.utc().isAfter(cutoff)) {
          return -1;
        }
      }

      return orderCutOffDays;
    },
    [ cutoffByDate ]
  );

  return daysToCutOff;
};

export default useDaysToCutoff;
